import React, { useEffect, useState } from 'react';
import { differenceInSeconds, parse, startOfDay, getDay, isBefore, isAfter } from 'date-fns';
import { Power } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

// Tipagem da configuração (deve bater com o JSON salvo no banco)
type DaySchedule = {
    enabled: boolean;
    onTime: string;
    offTime: string;
    label: string;
};

type ScheduleConfig = {
    [key: string]: DaySchedule;
};

interface ShutdownOverlayProps {
    isActive: boolean;
    schedule: ScheduleConfig;
    isPreviewMode?: boolean;
    isManuallyOn?: boolean; // controle manual de ligar/desligar
    userId?: string; // para sincronizar o toggle no banco
    onManualToggleSync?: (newValue: boolean) => void; // callback para sincronizar UI
}

type SystemState = 'ACTIVE' | 'WARNING_OFF' | 'WARNING_ON' | 'OFF' | 'OFF_MESSAGE' | 'OFF_BLACK' | 'MANUAL_OFF';

const ShutdownOverlay: React.FC<ShutdownOverlayProps> = ({
    isActive,
    schedule,
    isPreviewMode,
    isManuallyOn = true,
    userId,
    onManualToggleSync
}) => {


    const [state, setState] = useState<SystemState>('ACTIVE');
    const [countdown, setCountdown] = useState(0);
    const [offMessageTimer, setOffMessageTimer] = useState(5); // Timer de 5 segundos para mensagem
    const prevStateRef = React.useRef<SystemState>('ACTIVE');

    // Sincronizar toggle no banco quando o agendamento muda o estado
    const syncToggleToDatabase = async (newValue: boolean) => {
        if (!userId) return;

        try {
            await supabase
                .from('user_shutdown_schedules')
                .update({
                    is_manually_on: newValue,
                    updated_at: new Date().toISOString()
                })
                .eq('user_id', userId);

            console.log(`🔄 [SHUTDOWN SYNC] Toggle sincronizado no banco: ${newValue ? 'LIGADO' : 'DESLIGADO'}`);

            // Notificar a UI (callback opcional)
            if (onManualToggleSync) {
                onManualToggleSync(newValue);
            }
        } catch (error) {
            console.error('[SHUTDOWN SYNC] Erro ao sincronizar toggle:', error);
        }
    };

    // Monitorar transição para ACTIVE (Ligar) - inclui OFF_BLACK agora
    useEffect(() => {
        const prev = prevStateRef.current;
        // Se estava desligado (qualquer estado OFF) e agora ativou:
        if ((prev === 'OFF' || prev === 'OFF_BLACK' || prev === 'OFF_MESSAGE' || prev === 'WARNING_ON') && state === 'ACTIVE') {
            console.log('🔄 [SHUTDOWN] Sistema LIGOU pelo agendamento! Sincronizando toggle e recarregando...');
            // Sincronizar toggle para LIGADO
            syncToggleToDatabase(true);
            // Pequeno delay para garantir que o estado ACTIVE renderizou (sumindo overlay) antes do reload
            setTimeout(() => {
                window.location.reload();
            }, 500);
        }

        // Se estava ligado e agora desligou pelo agendamento (não manual)
        if (prev === 'ACTIVE' && (state === 'OFF_MESSAGE' || state === 'OFF_BLACK')) {
            console.log('🔄 [SHUTDOWN] Sistema DESLIGOU pelo agendamento! Sincronizando toggle...');
            // Sincronizar toggle para DESLIGADO
            syncToggleToDatabase(false);
        }

        prevStateRef.current = state;
    }, [state]);

    useEffect(() => {
        // Se a funcionalidade "Master" de agendamento estiver desligada nas configs
        if (!isActive) {
            // Se o usuário desligou manualmente, respeitamos
            if (!isManuallyOn) {
                if (state !== 'MANUAL_OFF') setState('MANUAL_OFF');
                return;
            }
            // Se manual tá on e agendamento off, sistema sempre ativo
            if (state !== 'ACTIVE') setState('ACTIVE');
            return;
        }

        // Se estiver desligado manualmente, AINDA PRECISAMOS VERIFICAR O AGENDAMENTO
        // para saber se chegou a hora de ligar (onTime)
        // Portanto, NÃO retornamos early aqui. Apenas logamos.
        if (!isManuallyOn) {
            // Se já estamos em MANUAL_OFF, ótimo. Se não, vamos deixar a lógica abaixo decidir
            // ou forçar depois se não for hora de ligar.
            // O importante é NÃO dar return para permitir que o checkSchedule detecte o 'onTime'
            // console.log('⚠️ [SHUTDOWN] Player em modo manual OFF, verificando se deve ligar pelo agendamento...');
        }

        const checkSchedule = () => {
            const now = new Date();

            // Mapear dia da semana
            const daysMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const currentDayKey = daysMap[getDay(now)];
            const dayConfig = schedule[currentDayKey];

            // LOG DEBUG - Removido para produção
            // console.log('🔍 [SHUTDOWN DEBUG]', {
            //     now: now.toLocaleTimeString(),
            //     currentDay: currentDayKey,
            //     dayConfig: dayConfig,
            //     isActive: isActive,
            //     scheduleKeys: Object.keys(schedule)
            // });

            // Se o dia não estiver habilitado na agenda, assume funcionamento normal (ACTIVE)
            if (!dayConfig || !dayConfig.enabled) {
                if (state !== 'ACTIVE') {
                    console.log('⚪ [SHUTDOWN] Dia não configurado ou desabilitado. Estado: ACTIVE');
                    setState('ACTIVE');
                }
                return;
            }

            // Parse dos horários de ligar/desligar para objetos Date hoje
            const todayStart = startOfDay(now);
            const onTimeDate = parse(dayConfig.onTime, 'HH:mm', todayStart);
            const offTimeDate = parse(dayConfig.offTime, 'HH:mm', todayStart);

            const secondsToOff = differenceInSeconds(offTimeDate, now);
            const secondsToOn = differenceInSeconds(onTimeDate, now);

            // LOG DEBUG DETALHADO - Removido para produção
            // console.log('🔍 [SHUTDOWN HORÁRIOS]', {
            //     onTime: dayConfig.onTime,
            //     offTime: dayConfig.offTime,
            //     onTimeDate: onTimeDate.toLocaleTimeString(),
            //     offTimeDate: offTimeDate.toLocaleTimeString(),
            //     secondsToOff: secondsToOff,
            //     secondsToOn: secondsToOn,
            //     currentState: state
            // });

            // LOGICA DE WINDOW (JANELA DE ATIVIDADE)
            // onTime = horário de ligar, offTime = horário de desligar
            // Se agora está entre onTime e offTime => ACTIVE ou WARNING_OFF

            // Detectar se a janela cruza a meia-noite (offTime é antes de onTime)
            const crossesMidnight = isBefore(offTimeDate, onTimeDate);

            // Verificação robusta de janela de atividade
            const isAfterOnTime = isAfter(now, onTimeDate) || now.getTime() === onTimeDate.getTime();
            const isBeforeOffTime = isBefore(now, offTimeDate);

            // Calcular se está dentro da janela ativa
            let isInsideWindow: boolean;
            if (crossesMidnight) {
                // Janela cruza meia-noite: está dentro se (agora >= onTime OU agora < offTime)
                isInsideWindow = isAfterOnTime || isBeforeOffTime;
            } else {
                // Janela normal: está dentro se (agora >= onTime E agora < offTime)
                isInsideWindow = isAfterOnTime && isBeforeOffTime;
            }

            // Verificar se já passou do horário de desligar
            let isAfterOffTime: boolean;
            if (crossesMidnight) {
                // Se cruza meia-noite, "passou do offTime" significa estar ENTRE offTime e onTime
                isAfterOffTime = !isInsideWindow;
            } else {
                // Janela normal: passou se agora >= offTime
                isAfterOffTime = isAfter(now, offTimeDate) || now.getTime() === offTimeDate.getTime();
            }

            // console.log('🔍 [SHUTDOWN JANELA]', {
            //     crossesMidnight: crossesMidnight,
            //     isInsideWindow: isInsideWindow,
            //     isAfterOn: isAfterOnTime,
            //     isBeforeOff: isBeforeOffTime,
            //     isAfterOff: isAfterOffTime,
            //     nowTime: now.toLocaleTimeString(),
            //     onTime: onTimeDate.toLocaleTimeString(),
            //     offTime: offTimeDate.toLocaleTimeString()
            // });

            if (isInsideWindow) {
                // Estamos dentro do horário de funcionamento
                // Checar se está quase acabando (faltando 60s ou menos para desligar)
                if (secondsToOff > 0 && secondsToOff <= 60) {
                    if (state !== 'WARNING_OFF') {
                        console.log('⚠️ [SHUTDOWN] Entrando em WARNING_OFF. Desliga em:', secondsToOff);
                        setState('WARNING_OFF');
                    }
                    setCountdown(secondsToOff);
                    return;
                }

                // Janela segura - player ativo
                // IMPORTANTE: Refinamento da lógica Manual vs Automático
                if (state !== 'ACTIVE') {
                    // Se estiver LIGADO manualmente, forçamos ACTIVE (comportamento normal)
                    if (isManuallyOn) {
                        console.log('✅ [SHUTDOWN] Horário permitido e Manual ON. Estado: ACTIVE');
                        setState('ACTIVE');
                    }
                    else {
                        // Se estiver DESLIGADO manualmente (!isManuallyOn)
                        // Só ligamos automaticamente se estivermos no INÍCIO do período (tolerância de 5 minutos)
                        // Isso permite ligar de manhã, mas respeita se o usuário desligar a tarde.
                        // secondsToOn é negativo quando já passou do horário.
                        const toleranceSeconds = 300; // 5 minutos
                        const diff = Math.abs(differenceInSeconds(now, onTimeDate)); // Diferença absoluta em segundos

                        // Se estivermos dentro dos primeiros 5 minutos do horário de início
                        if (diff <= toleranceSeconds) {
                            console.log('✅ [SHUTDOWN] Início de expediente detectado (dentro da tolerância). Forçando ON.');
                            setState('ACTIVE');
                        } else {
                            // Se já passou do início e está manual off, respeitamos o OFF
                            // Mas precisamos garantir que não fique num estado limbo
                            if (state !== 'MANUAL_OFF') {
                                console.log('🔴 [SHUTDOWN] Horário permitido mas Manual OFF (fora da tolerância de início). Mantendo desligado.');
                                setState('MANUAL_OFF');
                            }
                        }
                    }
                }
            }
            else if (isAfterOffTime) {
                // Já passou do horário de desligar - DEVE DESLIGAR
                // Se ainda não está em estado de desligamento, iniciar transição
                if (state !== 'OFF_MESSAGE' && state !== 'OFF_BLACK') {
                    console.log('🔴 [SHUTDOWN] Passou do horário de desligar. Estado: OFF_MESSAGE');
                    setState('OFF_MESSAGE');
                    setOffMessageTimer(5); // Resetar timer de 5 segundos
                }
            }
            else if (isBefore(now, onTimeDate)) {
                // Estamos ANTES do horário de ligar (madrugada/manhã cedo)

                // Checar se faltam 60s ou menos para ligar
                if (secondsToOn > 0 && secondsToOn <= 60) {
                    if (state !== 'WARNING_ON') {
                        console.log('⚠️ [SHUTDOWN] Entrando em WARNING_ON. Liga em:', secondsToOn);
                        setState('WARNING_ON');
                    }
                    setCountdown(secondsToOn);
                    return;
                }

                // Se secondsToOn é <= 0, significa que CHEGOU a hora de ligar!
                // Isso acontece exatamente no momento do onTime
                if (secondsToOn <= 0) {
                    console.log('🟢 [SHUTDOWN] Chegou exatamente no horário de ligar! secondsToOn:', secondsToOn);
                    if (state !== 'ACTIVE') {
                        console.log('✅ [SHUTDOWN] Transição para ACTIVE no momento exato do onTime');
                        setState('ACTIVE');
                    }
                    return;
                }

                // Ainda não chegou a hora de ligar - tela preta total
                if (state !== 'OFF_BLACK' && state !== 'OFF_MESSAGE') {
                    console.log('🔴 [SHUTDOWN] Antes do horário de ligar. Estado: OFF_BLACK');
                    setState('OFF_BLACK');
                }
            }
        };

        // Check imediato e depois intervalo
        checkSchedule();
        const timer = setInterval(checkSchedule, 1000);

        return () => clearInterval(timer);
    }, [isActive, isManuallyOn, schedule, state]);

    // Timer para transição de OFF_MESSAGE (5 segundos) para OFF_BLACK (tela preta)
    useEffect(() => {
        if (state !== 'OFF_MESSAGE') return;

        if (offMessageTimer <= 0) {
            console.log('🔴 [SHUTDOWN] Timer OFF_MESSAGE concluído. Indo para tela preta (OFF_BLACK)');
            setState('OFF_BLACK');
            return;
        }

        const timer = setInterval(() => {
            setOffMessageTimer(prev => {
                const newValue = prev - 1;
                console.log(`🕐 [SHUTDOWN] OFF_MESSAGE timer: ${newValue}s`);
                return newValue;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [state, offMessageTimer]);

    // Renderizar painel de debug apenas em desenvolvimento (comentar em produção)
    const debugPanel = process.env.NODE_ENV === 'development' ? (
        <div className="fixed top-0 left-0 bg-black/80 text-green-400 p-2 text-xs z-[2147483647] font-mono pointer-events-none select-none opacity-50 hover:opacity-100 transition-opacity">
            <p>🕒 Agora: {new Date().toLocaleTimeString()}</p>
            <p>⚡ Status: {state} {isPreviewMode ? '(PREVIEW - Sem Bloqueio)' : ''}</p>
            <p>⏱️ Countdown: {countdown}s</p>
            {schedule[Object.keys(schedule).find(k => k === ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][getDay(new Date())]) || ''] ? (
                <>
                    <p>🟢 Liga: {schedule[['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][getDay(new Date())]].onTime}</p>
                    <p>🔴 Desliga: {schedule[['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][getDay(new Date())]].offTime}</p>
                </>
            ) : <p>📅 Sem config hoje</p>}
        </div>
    ) : null;

    // LOG DEBUG - Removido para produção
    // console.log('🔍 [SHUTDOWN OVERLAY] Render check:', { isManuallyOn, state, isActive, isPreviewMode });

    // Se desligado manualmente (estado MANUAL_OFF), renderizar tela preta
    // NOTE: Verificamos apenas o STATE, não o isManuallyOn diretamente,
    // porque o state já é controlado pelo useEffect acima baseado em isManuallyOn
    if (state === 'MANUAL_OFF') {
        // Se estiver em preview, não mostra a tela preta
        if (isPreviewMode) {
            return debugPanel;
        }

        // Tela totalmente preta quando desligado manualmente
        return (
            <>
                {debugPanel}
                <div className="fixed inset-0 bg-black z-[2147483647] flex items-center justify-center">
                    {/* Tela completamente preta - simula TV desligada */}
                </div>
            </>
        );
    }

    if (!isActive) return null;

    // 1. Estado ACTIVE (Ligado): retorna null para não bloquear nada
    if (state === 'ACTIVE') return debugPanel;

    // 2. Estado OFF (Desligado/Bloqueado) - Tela com logo Watch
    if (state === 'OFF') {
        // Se estiver em PREVIEW, NÃO mostramos o bloqueio para permitir visualização
        if (isPreviewMode) {
            return debugPanel;
        }

        // Player REAL: Tela de desligamento com logo Watch
        return (
            <>
                {debugPanel}
                <div className="fixed inset-0 bg-gradient-to-br from-[#0A3622] to-[#071E14] z-[2147483647] flex flex-col items-center justify-center text-white font-sans">
                    <div className="text-center space-y-6 animate-in fade-in duration-500">
                        {/* Logo Watch */}
                        <div className="relative w-40 h-40 mx-auto opacity-30">
                            <div className="absolute inset-0 bg-[#39b92c] opacity-10 blur-xl rounded-full"></div>
                            <img
                                src="https://locaninja.com/logotipo-watch-white.png"
                                alt="Watch Logo"
                                className="w-full h-full object-contain"
                            />
                        </div>

                        <div className="space-y-2">
                            <p className="text-lg opacity-40 uppercase tracking-wider">Fora do horário de exibição</p>
                            <div className="flex items-center justify-center gap-2 opacity-30">
                                <Power className="h-4 w-4" />
                                <span className="text-sm">Sistema em espera</span>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // 3. Estado OFF_MESSAGE (Mensagem por 5 segundos antes de tela preta)
    if (state === 'OFF_MESSAGE') {
        if (isPreviewMode) {
            return debugPanel;
        }

        return (
            <>
                {debugPanel}
                <div className="fixed inset-0 bg-gradient-to-br from-[#0A3622] to-[#071E14] z-[2147483647] flex flex-col items-center justify-center text-white font-sans">
                    <div className="text-center space-y-6 animate-in fade-in duration-500">
                        {/* Logo Watch */}
                        <div className="relative w-40 h-40 mx-auto opacity-30">
                            <div className="absolute inset-0 bg-[#39b92c] opacity-10 blur-xl rounded-full"></div>
                            <img
                                src="https://locaninja.com/logotipo-watch-white.png"
                                alt="Watch Logo"
                                className="w-full h-full object-contain"
                            />
                        </div>

                        <div className="space-y-4">
                            <p className="text-2xl opacity-60 uppercase tracking-wider">Fora do horário de exibição</p>
                            <div className="flex items-center justify-center gap-2 opacity-40">
                                <Power className="h-5 w-5" />
                                <span className="text-lg">Desligando em {offMessageTimer}s</span>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // 4. Estado OFF_BLACK (Tela completamente preta)
    if (state === 'OFF_BLACK') {
        if (isPreviewMode) {
            return debugPanel;
        }

        // Tela totalmente preta - simula TV desligada
        return (
            <>
                {debugPanel}
                <div className="fixed inset-0 bg-black z-[2147483647] flex items-center justify-center">
                    {/* Tela completamente preta */}
                </div>
            </>
        );
    }

    // 3. Estados de WARNING (WARNING_OFF / WARNING_ON) - Contagem Regressiva
    // Estes serão exibidos em AMBOS os modos (Normal e Preview)

    // Determinar textos conforme especificação
    const isShuttingDown = state === 'WARNING_OFF';
    const notificationText = isShuttingDown
        ? 'O sistema desligará em...'
        : 'A transmissão iniciará em...';
    const titleText = isShuttingDown
        ? 'Desligamento Programado'
        : 'Iniciando Transmissão';

    // Tela de Transição (Verde)
    return (
        <div className="fixed inset-0 bg-gradient-to-br from-[#0A3622] to-[#071E14] z-[2147483647] flex flex-col items-center justify-center text-white font-sans">
            {debugPanel}
            <div className="text-center space-y-8 animate-in fade-in duration-500">
                {/* Logo Watch */}
                <div className="mb-8 relative w-48 h-48 mx-auto">
                    <div className="absolute inset-0 bg-[#39b92c] opacity-20 blur-xl rounded-full"></div>
                    <img
                        src="https://locaninja.com/logotipo-watch-white.png"
                        alt="Watch Logo"
                        className="w-full h-full object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]"
                    />
                </div>

                <div className="space-y-4">
                    {/* Título */}
                    <h2 className="text-xl md:text-2xl font-light opacity-70 uppercase tracking-wider">
                        {titleText}
                    </h2>

                    {/* Notificação */}
                    <p className="text-lg md:text-xl opacity-90 tracking-wide">
                        {notificationText}
                    </p>

                    {/* Contador grande */}
                    <div className="text-[100px] md:text-[140px] font-bold leading-none tabular-nums tracking-tight text-[#39b92c]">
                        {countdown.toString().padStart(2, '0')}
                    </div>

                    <p className="text-sm opacity-60 uppercase tracking-widest">Segundos</p>
                </div>
            </div>
        </div>
    );
};

export default ShutdownOverlay;

